import { useEffect, useState } from 'react';
import { buildColorFilterCss } from '../../pdf/shared/pdfPageEditEngine';

/**
 * Page thumbnail that mirrors crop + rotation + flatten edits in the preview grid.
 * cropRegion: normalized AABB { left, top, width, height } relative to the page.
 * cropCorners: normalized TL,TR,BR,BL — drawn as overlay when no flattenedSrc yet.
 * flattenedSrc: perspective-warped preview (preferred for flatten mode).
 */
export default function PdfPageThumb({
    src,
    alt = '',
    cropRegion = null,
    cropCorners = null,
    flattenedSrc = null,
    rotation = 0,
    removed = false,
    brightness = 100,
    contrast = 100,
    saturation = 100,
    grayscale = 0,
    sepia = 0,
    placeholder = 'PDF',
}) {
    const rot = ((Number(rotation) || 0) % 360 + 360) % 360;
    const hasRotation = rot !== 0;
    const previewSrc = flattenedSrc || src;
    const filterCss = buildColorFilterCss({ brightness, contrast, saturation, grayscale, sepia });
    const colorStyle = filterCss ? { filter: filterCss } : undefined;
    const crop = !flattenedSrc && cropRegion?.width > 0 && cropRegion?.height > 0
        ? cropRegion
        : null;
    const corners = !flattenedSrc && Array.isArray(cropCorners) && cropCorners.length === 4
        ? cropCorners
        : null;

    // Page / flatten image aspect (width ÷ height). Needed so crop fractions map to real size.
    const [mediaAspect, setMediaAspect] = useState(3 / 4);

    useEffect(() => {
        if (!previewSrc) return undefined;
        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (cancelled) return;
            const w = img.naturalWidth || 0;
            const h = img.naturalHeight || 0;
            if (w > 0 && h > 0) setMediaAspect(w / h);
        };
        img.src = previewSrc;
        return () => { cancelled = true; };
    }, [previewSrc]);

    if (!previewSrc) {
        return (
            <div className="flex aspect-[3/4] h-full w-full items-center justify-center px-3 text-center text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                {placeholder}
            </div>
        );
    }

    if (flattenedSrc) {
        return (
            <div
                className="relative flex w-full items-center justify-center overflow-hidden bg-[#F7F5F3] dark:bg-zinc-800"
                style={{ aspectRatio: String(Math.max(mediaAspect, 0.05)) }}
            >
                <img
                    src={flattenedSrc}
                    alt={alt}
                    className={`h-full w-full object-contain transition-opacity ${removed ? 'opacity-40' : ''}`}
                    style={{
                        transform: hasRotation ? `rotate(${rot}deg)` : undefined,
                        ...colorStyle,
                    }}
                    draggable={false}
                />
            </div>
        );
    }

    if (crop) {
        // crop.width/height are fractions of page W/H — convert to real pixel aspect.
        const cropAspect = (Math.max(crop.width, 0.01) / Math.max(crop.height, 0.01)) * mediaAspect;
        return (
            <div
                className="relative w-full overflow-hidden bg-[#F7F5F3] dark:bg-zinc-800"
                style={{ aspectRatio: String(Math.max(cropAspect, 0.05)) }}
            >
                <div
                    className="absolute inset-0 overflow-hidden"
                    style={{
                        transform: hasRotation ? `rotate(${rot}deg)` : undefined,
                        transformOrigin: 'center center',
                    }}
                >
                    <img
                        src={src}
                        alt={alt}
                        className={`absolute max-w-none transition-opacity ${removed ? 'opacity-40' : ''}`}
                        style={{
                            width: `${100 / crop.width}%`,
                            height: `${100 / crop.height}%`,
                            left: `${(-crop.left / crop.width) * 100}%`,
                            top: `${(-crop.top / crop.height) * 100}%`,
                            ...colorStyle,
                        }}
                        draggable={false}
                    />
                </div>
                {corners ? (
                    <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1 1" preserveAspectRatio="none">
                        <polygon
                            points={corners.map((c) => `${(c.x - crop.left) / crop.width},${(c.y - crop.top) / crop.height}`).join(' ')}
                            fill="rgba(255,152,0,0.12)"
                            stroke="#ff9800"
                            strokeWidth={0.012}
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                ) : null}
            </div>
        );
    }

    return (
        <div
            className="relative flex w-full items-center justify-center overflow-hidden bg-[#F7F5F3] dark:bg-zinc-800"
            style={{ aspectRatio: String(Math.max(mediaAspect, 0.05)) }}
        >
            <img
                src={src}
                alt={alt}
                className={`h-full w-full object-contain transition-opacity ${removed ? 'opacity-40' : ''}`}
                style={{
                    transform: hasRotation ? `rotate(${rot}deg)` : undefined,
                    ...colorStyle,
                }}
                draggable={false}
            />
            {corners ? (
                <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1 1" preserveAspectRatio="none">
                    <path
                        d={`M0,0 H1 V1 H0 Z ${corners.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ')} Z`}
                        fill="rgba(0,0,0,0.4)"
                        fillRule="evenodd"
                    />
                    <polygon
                        points={corners.map((c) => `${c.x},${c.y}`).join(' ')}
                        fill="none"
                        stroke="#ff9800"
                        strokeWidth={0.014}
                        strokeDasharray="0.03 0.02"
                        vectorEffect="non-scaling-stroke"
                    />
                    {corners.map((c, i) => (
                        <circle
                            key={i}
                            cx={c.x}
                            cy={c.y}
                            r={0.028}
                            fill="#fff"
                            stroke={['#4ECDC4', '#845EF7', '#FF6B6B', '#FFD43B'][i]}
                            strokeWidth={0.01}
                        />
                    ))}
                </svg>
            ) : null}
        </div>
    );
}
